import { deflateRaw, inflateRaw } from "pako";

import { PROTOCOL_VERSION, sha256Hex, utf8 } from "../protocol/wallet-approval-core.mjs";

export const QR_TRANSPORT_TYPE = "IPI_WALLET_QR";
export const QR_TRANSPORT_VERSION = 3;
export const QR_PAYLOAD_TYPES = Object.freeze({ REQUEST: "REQUEST", RESPONSE: "RESPONSE" });
// Medium correction keeps enough recovery for the small center mark while
// materially reducing the module count for dense signed response payloads.
export const QR_ERROR_CORRECTION = "M";
export const QR_MAX_TEXT_BYTES = 200;
export const QR_MULTIPART_CHUNK_LENGTH = 160;
export const QR_FRAME_INTERVAL_MS = 1_200;

const BASE45_ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:";
const BASE45_VALUES = new Map([...BASE45_ALPHABET].map((character, index) => [character, index]));
const QR_COMPRESSION_DICTIONARY = utf8(
  '{"protocol_name":"IPI_WALLET_APPROVAL","protocol_version":2,"request_id":"",'
  + '"checkout_id":"","terminal_id":"terminal-dev-001","merchant_id":"merchant-ipi-demo",'
  + '"created_at_ms":,"expires_at_ms":,"denomination":"aipi","total_amount":"","item_count":,'
  + '"item_summary_hash":"","ipi_card_id":"IPI-CARD-","card_verified_at_ms":,'
  + '"card_verification_config_id":"nxp-an12196-development","nonce":"",'
  + '"chain_id":"ipi-testnet-1","response_channel":"local-development","request_hash":"",'
  + '"response_id":"","wallet_public_key_id":"","decision":"APPROVED","responded_at_ms":,'
  + '"rejection_reason":null,"signature_algorithm":"secp256k1-sha256-p1363-v1","signature":""}',
);
const V1_ENVELOPE_KEYS = Object.freeze([
  "transport_type", "transport_version", "protocol_version", "payload_type",
  "encoded_payload", "payload_hash",
]);

function assertExactKeys(value, expected, name) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  const actual = Object.keys(value).sort();
  const keys = [...expected].sort();
  if (actual.length !== keys.length || actual.some((key, index) => key !== keys[index])) {
    throw new TypeError(`${name} schema is invalid`);
  }
}

function base64UrlDecode(value) {
  if (typeof value !== "string" || !value || !/^[A-Za-z0-9_-]+$/u.test(value)) {
    throw new TypeError("encoded payload is not unpadded Base64URL");
  }
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  let binary;
  try {
    binary = atob(value.replace(/-/g, "+").replace(/_/g, "/") + padding);
  } catch {
    throw new TypeError("encoded payload is not valid Base64URL");
  }
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function canonicalV1Envelope(envelope) {
  return JSON.stringify({
    transport_type: envelope.transport_type,
    transport_version: envelope.transport_version,
    protocol_version: envelope.protocol_version,
    payload_type: envelope.payload_type,
    encoded_payload: envelope.encoded_payload,
    payload_hash: envelope.payload_hash,
  });
}

export function base45Encode(bytes) {
  if (!(bytes instanceof Uint8Array)) throw new TypeError("Base45 input must be bytes");
  let encoded = "";
  for (let index = 0; index < bytes.length; index += 2) {
    if (index + 1 < bytes.length) {
      let value = bytes[index] * 256 + bytes[index + 1];
      const first = value % 45;
      value = Math.floor(value / 45);
      const second = value % 45;
      const third = Math.floor(value / 45);
      encoded += BASE45_ALPHABET[first] + BASE45_ALPHABET[second] + BASE45_ALPHABET[third];
    } else {
      const first = bytes[index] % 45;
      const second = Math.floor(bytes[index] / 45);
      encoded += BASE45_ALPHABET[first] + BASE45_ALPHABET[second];
    }
  }
  return encoded;
}

export function base45Decode(value) {
  if (typeof value !== "string" || !value || value.length % 3 === 1) {
    throw new TypeError("encoded payload is not valid Base45");
  }
  const bytes = [];
  for (let index = 0; index < value.length;) {
    const remaining = value.length - index;
    const first = BASE45_VALUES.get(value[index]);
    const second = BASE45_VALUES.get(value[index + 1]);
    if (first === undefined || second === undefined) {
      throw new TypeError("encoded payload is not valid Base45");
    }
    if (remaining >= 3) {
      const third = BASE45_VALUES.get(value[index + 2]);
      if (third === undefined) throw new TypeError("encoded payload is not valid Base45");
      const decoded = first + second * 45 + third * 45 * 45;
      if (decoded > 0xffff) throw new TypeError("encoded payload is not valid Base45");
      bytes.push(Math.floor(decoded / 256), decoded % 256);
      index += 3;
    } else {
      const decoded = first + second * 45;
      if (decoded > 0xff) throw new TypeError("encoded payload is not valid Base45");
      bytes.push(decoded);
      index += 2;
    }
  }
  return Uint8Array.from(bytes);
}

function payloadTypeCode(payloadType) {
  if (payloadType === QR_PAYLOAD_TYPES.REQUEST) return "Q";
  if (payloadType === QR_PAYLOAD_TYPES.RESPONSE) return "S";
  throw new TypeError("unsupported QR payload type");
}

function payloadTypeFromCode(code) {
  if (code === "Q") return QR_PAYLOAD_TYPES.REQUEST;
  if (code === "S") return QR_PAYLOAD_TYPES.RESPONSE;
  throw new TypeError("unsupported payload type");
}

function v2EnvelopeText(payloadType, protocolVersion, encodedPayload, payloadHash) {
  return `IPIQR2:${payloadTypeCode(payloadType)}:${protocolVersion}:${encodedPayload}:${payloadHash.toUpperCase()}`;
}

function v3EnvelopeText(payloadType, protocolVersion, encodedPayload, payloadChecksum) {
  return `IPIQR3:${payloadTypeCode(payloadType)}:${protocolVersion}:${encodedPayload}:${payloadChecksum}`;
}

export function encodeQrEnvelope(payloadBytes, payloadType, protocolVersion = PROTOCOL_VERSION) {
  if (!(payloadBytes instanceof Uint8Array) || payloadBytes.length === 0) {
    throw new TypeError("canonical payload bytes are required");
  }
  payloadTypeCode(payloadType);
  if (!Number.isSafeInteger(protocolVersion) || protocolVersion <= 0) {
    throw new TypeError("protocol version must be a positive integer");
  }
  const compressed = deflateRaw(payloadBytes, {
    level: 9,
    dictionary: QR_COMPRESSION_DICTIONARY,
  });
  const encodedPayload = base45Encode(compressed);
  const payloadHash = sha256Hex(payloadBytes).toUpperCase();
  const payloadChecksum = payloadHash.slice(0, 32);
  const text = v3EnvelopeText(payloadType, protocolVersion, encodedPayload, payloadChecksum);
  return {
    envelope: {
      transport_type: QR_TRANSPORT_TYPE,
      transport_version: QR_TRANSPORT_VERSION,
      protocol_version: protocolVersion,
      payload_type: payloadType,
      payload_encoding: "BASE45+DEFLATE_RAW+IPI_DICT_V1",
      encoded_payload: encodedPayload,
      payload_hash: payloadHash,
    },
    text,
    byte_length: utf8(text).length,
    payload_byte_length: payloadBytes.length,
    compressed_byte_length: compressed.length,
  };
}

function decodeV3Envelope(text, expectedType) {
  if (!text.startsWith("IPIQR3:")) throw new TypeError("unsupported transport type");
  const typeSeparator = text.indexOf(":", 7);
  const versionSeparator = text.indexOf(":", typeSeparator + 1);
  const checksumSeparator = text.lastIndexOf(":");
  if (typeSeparator < 0 || versionSeparator < 0 || checksumSeparator <= versionSeparator) {
    throw new TypeError("QR envelope is malformed");
  }
  const payloadType = payloadTypeFromCode(text.slice(7, typeSeparator));
  const protocolText = text.slice(typeSeparator + 1, versionSeparator);
  if (!/^[1-9][0-9]*$/u.test(protocolText)) throw new TypeError("invalid protocol version");
  const protocolVersion = Number(protocolText);
  if (!Number.isSafeInteger(protocolVersion)) throw new TypeError("invalid protocol version");
  const encodedPayload = text.slice(versionSeparator + 1, checksumSeparator);
  const payloadChecksum = text.slice(checksumSeparator + 1);
  if (!/^[0-9A-F]{32}$/u.test(payloadChecksum)) {
    throw new TypeError("QR payload checksum is invalid");
  }
  if (expectedType && payloadType !== expectedType) throw new TypeError(`expected ${expectedType} payload`);
  let payload;
  try {
    payload = inflateRaw(base45Decode(encodedPayload), { dictionary: QR_COMPRESSION_DICTIONARY });
  } catch (error) {
    if (error instanceof TypeError) throw error;
    throw new TypeError("compressed QR payload is invalid");
  }
  const payloadHash = sha256Hex(payload).toUpperCase();
  if (payloadHash.slice(0, 32) !== payloadChecksum) {
    throw new TypeError("QR payload checksum is invalid");
  }
  if (text !== v3EnvelopeText(payloadType, protocolVersion, encodedPayload, payloadChecksum)) {
    throw new TypeError("QR envelope is not canonical");
  }
  return {
    envelope: {
      transport_type: QR_TRANSPORT_TYPE,
      transport_version: QR_TRANSPORT_VERSION,
      protocol_version: protocolVersion,
      payload_type: payloadType,
      payload_encoding: "BASE45+DEFLATE_RAW+IPI_DICT_V1",
      encoded_payload: encodedPayload,
      payload_hash: payloadHash,
    },
    payload,
    payload_text: new TextDecoder().decode(payload),
  };
}

function decodeV2Envelope(text, expectedType) {
  if (!text.startsWith("IPIQR2:")) throw new TypeError("unsupported transport type");
  const typeSeparator = text.indexOf(":", 7);
  const versionSeparator = text.indexOf(":", typeSeparator + 1);
  const hashSeparator = text.lastIndexOf(":");
  if (typeSeparator < 0 || versionSeparator < 0 || hashSeparator <= versionSeparator) {
    throw new TypeError("QR envelope is malformed");
  }
  const typeCode = text.slice(7, typeSeparator);
  const payloadType = payloadTypeFromCode(typeCode);
  const protocolText = text.slice(typeSeparator + 1, versionSeparator);
  if (!/^[1-9][0-9]*$/u.test(protocolText)) throw new TypeError("invalid protocol version");
  const protocolVersion = Number(protocolText);
  if (!Number.isSafeInteger(protocolVersion)) throw new TypeError("invalid protocol version");
  const encodedPayload = text.slice(versionSeparator + 1, hashSeparator);
  const payloadHash = text.slice(hashSeparator + 1);
  if (!/^[0-9A-F]{64}$/u.test(payloadHash)) throw new TypeError("QR payload checksum is invalid");
  if (expectedType && payloadType !== expectedType) throw new TypeError(`expected ${expectedType} payload`);
  let payload;
  try {
    payload = inflateRaw(base45Decode(encodedPayload));
  } catch (error) {
    if (error instanceof TypeError) throw error;
    throw new TypeError("compressed QR payload is invalid");
  }
  if (sha256Hex(payload).toUpperCase() !== payloadHash) {
    throw new TypeError("QR payload checksum is invalid");
  }
  if (text !== v2EnvelopeText(payloadType, protocolVersion, encodedPayload, payloadHash)) {
    throw new TypeError("QR envelope is not canonical");
  }
  return {
    envelope: {
      transport_type: QR_TRANSPORT_TYPE,
      transport_version: 2,
      protocol_version: protocolVersion,
      payload_type: payloadType,
      payload_encoding: "BASE45+DEFLATE_RAW",
      encoded_payload: encodedPayload,
      payload_hash: payloadHash,
    },
    payload,
    payload_text: new TextDecoder().decode(payload),
  };
}

function decodeV1Envelope(text, expectedType) {
  let envelope;
  try { envelope = JSON.parse(text); } catch { throw new TypeError("QR envelope is malformed JSON"); }
  assertExactKeys(envelope, V1_ENVELOPE_KEYS, "QR envelope");
  if (text !== canonicalV1Envelope(envelope)) throw new TypeError("QR envelope is not canonical");
  if (envelope.transport_type !== QR_TRANSPORT_TYPE) throw new TypeError("unsupported transport type");
  if (envelope.transport_version !== 1) throw new TypeError("unsupported transport version");
  if (!Number.isSafeInteger(envelope.protocol_version) || envelope.protocol_version <= 0) {
    throw new TypeError("invalid protocol version");
  }
  if (!Object.values(QR_PAYLOAD_TYPES).includes(envelope.payload_type)) {
    throw new TypeError("unsupported payload type");
  }
  if (expectedType && envelope.payload_type !== expectedType) {
    throw new TypeError(`expected ${expectedType} payload`);
  }
  const payload = base64UrlDecode(envelope.encoded_payload);
  if (sha256Hex(payload) !== envelope.payload_hash) throw new TypeError("QR payload checksum is invalid");
  return { envelope, payload, payload_text: new TextDecoder().decode(payload) };
}

export function decodeQrEnvelope(text, expectedType) {
  if (typeof text !== "string" || !text) throw new TypeError("QR envelope is malformed");
  if (text.startsWith("IPIQR3:")) return decodeV3Envelope(text, expectedType);
  if (text.startsWith("IPIQR2:")) return decodeV2Envelope(text, expectedType);
  return decodeV1Envelope(text, expectedType);
}

function legacyFrameText(messageId, partIndex, totalParts, payloadHash, chunk) {
  return `IPIQR2P:${messageId}:${partIndex}:${totalParts}:${payloadHash}:${chunk}`;
}

function decodeLegacyQrFrame(text) {
  if (typeof text !== "string" || !text.startsWith("IPIQR2P:")) {
    throw new TypeError("unsupported multipart QR frame");
  }
  const fields = [];
  let start = 8;
  for (let index = 0; index < 4; index += 1) {
    const separator = text.indexOf(":", start);
    if (separator < 0) throw new TypeError("multipart QR frame is malformed");
    fields.push(text.slice(start, separator));
    start = separator + 1;
  }
  const [messageId, partText, totalText, payloadHash] = fields;
  const chunk = text.slice(start);
  if (!/^[0-9A-F]{16}$/u.test(messageId) || !/^[0-9A-F]{64}$/u.test(payloadHash)
      || !/^[0-9]+$/u.test(partText) || !/^[0-9]+$/u.test(totalText) || !chunk) {
    throw new TypeError("multipart QR frame is malformed");
  }
  const partIndex = Number(partText);
  const totalParts = Number(totalText);
  if (!Number.isSafeInteger(partIndex) || !Number.isSafeInteger(totalParts)
      || partIndex < 0 || totalParts < 2 || partIndex >= totalParts) {
    throw new TypeError("multipart QR frame indexes are invalid");
  }
  if (text !== legacyFrameText(messageId, partIndex, totalParts, payloadHash, chunk)) {
    throw new TypeError("multipart QR frame is not canonical");
  }
  return {
    frame_version: 2, message_id: messageId, part_index: partIndex,
    total_parts: totalParts, payload_hash: payloadHash, chunk,
  };
}

function compactFrameChecksum(messageId, partIndex, totalParts, chunk) {
  return sha256Hex(utf8(`${messageId}:${partIndex}:${totalParts}:${chunk}`))
    .slice(0, 8).toUpperCase();
}

function base36Code(value) {
  if (!Number.isSafeInteger(value) || value < 0 || value > 35) {
    throw new TypeError("multipart QR frame indexes are invalid");
  }
  return value.toString(36).toUpperCase();
}

function compactFrameText(messageId, partIndex, totalParts, chunk) {
  const indexes = `${base36Code(partIndex)}${base36Code(totalParts)}`;
  const checksum = compactFrameChecksum(messageId, partIndex, totalParts, chunk);
  return `IPIQR3P:${messageId}:${indexes}:${chunk}:${checksum}`;
}

function decodeCompactQrFrame(text) {
  if (typeof text !== "string" || !text.startsWith("IPIQR3P:")) {
    throw new TypeError("unsupported multipart QR frame");
  }
  const messageSeparator = text.indexOf(":", 8);
  const indexesSeparator = text.indexOf(":", messageSeparator + 1);
  const checksumSeparator = text.lastIndexOf(":");
  if (messageSeparator < 0 || indexesSeparator < 0 || checksumSeparator <= indexesSeparator) {
    throw new TypeError("multipart QR frame is malformed");
  }
  const messageId = text.slice(8, messageSeparator);
  const indexes = text.slice(messageSeparator + 1, indexesSeparator);
  const chunk = text.slice(indexesSeparator + 1, checksumSeparator);
  const checksum = text.slice(checksumSeparator + 1);
  if (!/^[0-9A-F]{16}$/u.test(messageId) || !/^[0-9A-Z]{2}$/u.test(indexes)
      || !chunk || !/^[0-9A-F]{8}$/u.test(checksum)) {
    throw new TypeError("multipart QR frame is malformed");
  }
  const partIndex = Number.parseInt(indexes[0], 36);
  const totalParts = Number.parseInt(indexes[1], 36);
  if (totalParts < 2 || partIndex >= totalParts) {
    throw new TypeError("multipart QR frame indexes are invalid");
  }
  if (checksum !== compactFrameChecksum(messageId, partIndex, totalParts, chunk)
      || text !== compactFrameText(messageId, partIndex, totalParts, chunk)) {
    throw new TypeError("multipart QR frame checksum is invalid");
  }
  return {
    frame_version: 3, message_id: messageId, part_index: partIndex,
    total_parts: totalParts, payload_hash: null, chunk,
  };
}

export function decodeQrFrame(text) {
  if (typeof text === "string" && text.startsWith("IPIQR3P:")) {
    return decodeCompactQrFrame(text);
  }
  return decodeLegacyQrFrame(text);
}

export function frameQrEnvelope(envelopeText, chunkLength = QR_MULTIPART_CHUNK_LENGTH) {
  if (typeof envelopeText !== "string" || !envelopeText) throw new TypeError("QR envelope is required");
  if (!Number.isSafeInteger(chunkLength) || chunkLength < 100) {
    throw new TypeError("multipart chunk length is invalid");
  }
  if (utf8(envelopeText).length <= QR_MAX_TEXT_BYTES) return [envelopeText];
  const checksum = sha256Hex(utf8(envelopeText)).toUpperCase();
  const totalParts = Math.ceil(envelopeText.length / chunkLength);
  const balancedChunkLength = Math.ceil(envelopeText.length / totalParts);
  const chunks = [];
  for (let offset = 0; offset < envelopeText.length; offset += balancedChunkLength) {
    chunks.push(envelopeText.slice(offset, offset + balancedChunkLength));
  }
  if (chunks.length > 35) throw new TypeError("multipart QR message has too many parts");
  return chunks.map((chunk, partIndex) => compactFrameText(
    checksum.slice(0, 16), partIndex, chunks.length, chunk,
  ));
}

export function reassembleQrFrames(frameTexts) {
  if (!Array.isArray(frameTexts) || frameTexts.length === 0) throw new TypeError("QR frames are required");
  if (frameTexts.length === 1
      && !frameTexts[0].startsWith("IPIQR2P:")
      && !frameTexts[0].startsWith("IPIQR3P:")) return frameTexts[0];
  const frames = frameTexts.map(decodeQrFrame);
  const first = frames[0];
  if (frames.some((frame) => frame.frame_version !== first.frame_version
      || frame.message_id !== first.message_id
      || frame.payload_hash !== first.payload_hash || frame.total_parts !== first.total_parts)) {
    throw new TypeError("mixed multipart QR messages are not allowed");
  }
  const byIndex = new Map();
  for (const frame of frames) {
    if (byIndex.has(frame.part_index)) throw new TypeError("duplicated multipart QR frame");
    byIndex.set(frame.part_index, frame);
  }
  if (byIndex.size !== first.total_parts) throw new TypeError("multipart QR message is incomplete");
  let envelopeText = "";
  for (let index = 0; index < first.total_parts; index += 1) envelopeText += byIndex.get(index).chunk;
  const checksum = sha256Hex(utf8(envelopeText)).toUpperCase();
  const checksumMatches = first.frame_version === 3
    ? checksum.slice(0, 16) === first.message_id
    : checksum === first.payload_hash;
  if (!checksumMatches) {
    throw new TypeError("multipart QR checksum is invalid");
  }
  return envelopeText;
}
